import '@/modules/utility/ds-wms/styles/wmsTable.css';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { contractorsApi } from '../../services/wmsService';
import DataGrid from '@/modules/utility/ds-wms/components/WmsDataGrid';
import ContractorForm from '../forms/ContractorForm';
import axiosInstance from '@/services/interceptor';
import DocumentUploader from '../DocumentUploader';
import ContractorInvoicesTab from './ContractorInvoicesTab';
import ContractorWorkflowsTab from './ContractorWorkflowsTab';
import ContractorPaymentsTab from './ContractorPaymentsTab';
import ContractorDetailsTab from './ContractorDetailsTab';
import WorkProfile from './WorkProfile';
import { todayYmd } from '../../lib/dateUtils';
import { TableSkeleton } from '@/components/base/Skeleton';
import { WMS_POPUP_HEADER, WMS_POPUP_ICON, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_BTN, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

interface ContractorProject {
  workId: number;
  workCode: string;
  workName: string;
  status: string;
  statusName: string;
  workType: string;
  udaName: string;
  districtName: string;
  location: string;
  sanctionedAmount: number;
  contractValue: number;
  physicalProgress: number;
  financialProgress: number;
  startDate: string;
  sector: string;
  scheme: string;
}

interface ContractorHeader {
  name: string;
  contractorId: number;
  contractorCode: string;
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  pinCode: string;
  registrationNo: string;
  gstNo: string;
}

interface ContractorProfileProps {
  isOpen: boolean;
  onClose: () => void;
  /** Only contractorId is required — the rest is loaded from the contractor master. */
  contractor: Partial<ContractorHeader> & { contractorId: number };
  /** Optional: without it the profile opens Work Profile itself. */
  onWorkIdClick?: (workId: string) => void;
}

const TABS = [
  { key: 'details', label: 'Details', icon: 'ri-profile-line' },
  { key: 'works', label: 'Works', icon: 'ri-building-line' },
  { key: 'payments', label: 'Payments & Dues', icon: 'ri-money-rupee-circle-line' },
  { key: 'documents', label: 'Documents', icon: 'ri-file-list-3-line' },
  { key: 'workflows', label: 'Workflows', icon: 'ri-flow-chart' },
  { key: 'invoices', label: 'Invoices', icon: 'ri-bill-line' },
  { key: 'users', label: 'Users', icon: 'ri-team-line' },
  { key: 'contacts', label: 'Contacts', icon: 'ri-contacts-book-line' },
] as const;

type TabKey = typeof TABS[number]['key'];

export default function ContractorProfile({ isOpen, onClose, contractor: contractorProp, onWorkIdClick: onWorkIdClickProp }: ContractorProfileProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const [workModalId, setWorkModalId] = useState('');
  const onWorkIdClick = onWorkIdClickProp ?? ((workId: string) => setWorkModalId(workId));

  // Full master record, so a caller that only knows the id still gets every detail.
  const [record, setRecord] = useState<Record<string, any> | null>(null);
  const [recordLoading, setRecordLoading] = useState(false);
  /* Bumped when the edit form saves. Without it the profile read the master record once
   * on open and never again, so a successful edit left the drawer showing the values
   * from before the save and the change looked as though it had not been applied. */
  const [recordNonce, setRecordNonce] = useState(0);
  useEffect(() => {
    const cId = contractorProp?.contractorId;
    if (!isOpen || !cId) return;
    let alive = true;
    setRecordLoading(true);
    contractorsApi.getById(cId)
      .then((r: any) => { if (alive) setRecord(r ?? null); })
      .catch(() => { if (alive) setRecord(null); })
      .finally(() => { if (alive) setRecordLoading(false); });
    return () => { alive = false; };
  }, [isOpen, contractorProp?.contractorId, recordNonce]);

  // Caller-supplied fields win; the master record fills whatever the caller didn't have.
  const contractor: ContractorHeader = {
    contractorId: contractorProp.contractorId,
    name: contractorProp.name || record?.cntrctr_nm || '',
    contractorCode: contractorProp.contractorCode || record?.cntrctr_cd || '',
    contactPerson: contractorProp.contactPerson || record?.cntct_prsn_nm || '',
    phone: contractorProp.phone || record?.phn_nu || '',
    email: contractorProp.email || record?.eml_tx || '',
    address: contractorProp.address || record?.addr_tx || '',
    city: contractorProp.city || record?.cty_nm || '',
    state: contractorProp.state || record?.stte_nm || '',
    pinCode: contractorProp.pinCode || record?.pin_cd || '',
    registrationNo: contractorProp.registrationNo || record?.rgstrn_nu || '',
    gstNo: contractorProp.gstNo || record?.gstn_nu || '',
  };
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [contractorProjects, setContractorProjects] = useState<ContractorProject[]>([]);
  const [, setLoading] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  // Users tab state
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [userForm, setUserForm] = useState({ usr_nm: '', dsply_nm: '', fst_nm: '', lst_nm: '', eml_tx: '', phn_nu: '', rle_cd: 'SUB', dsgns_tx: '', is_prmry_in: false, sts_cd: 'ACTIVE' });

  // Contacts tab state
  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<any>(null);
  const [contactForm, setContactForm] = useState({ cntct_nm: '', dsgntn_cd: '', dsgntn_nm: '', phn_nu: '', alt_phn_nu: '', eml_tx: '', is_prmry_in: false });

  // Documents tab — now uses DocumentUploader component

  const loadUsers = useCallback(async () => {
    if (!contractor?.contractorId) return;
    setUsersLoading(true);
    try {
      const response = await axiosInstance.get(`ds-wms/contractors/${contractor.contractorId}/users`);
      setUsers(response.data.data || []);
    } catch (e) { console.warn('Failed to load users:', e); }
    finally { setUsersLoading(false); }
  }, [contractor?.contractorId]);

  useEffect(() => {
    if (isOpen && activeTab === 'users') loadUsers();
  }, [isOpen, activeTab, loadUsers]);

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        await axiosInstance.put(`ds-wms/contractor-users/${editingUser.portal_usr_id}`, userForm);
      } else {
        await axiosInstance.post(`ds-wms/contractors/${contractor.contractorId}/users`, userForm);
      }
      setShowUserForm(false);
      setEditingUser(null);
      setUserForm({ usr_nm: '', dsply_nm: '', fst_nm: '', lst_nm: '', eml_tx: '', phn_nu: '', rle_cd: 'SUB', dsgns_tx: '', is_prmry_in: false, sts_cd: 'ACTIVE' });
      loadUsers();
    } catch (e) { console.error('Failed to save user:', e); }
  };

  // Contacts tab
  const CONTACT_DESIGNATIONS = [
    { code: 'PROPRIETOR', label: 'Proprietor' },
    { code: 'DIRECTOR', label: 'Director' },
    { code: 'CEO', label: 'CEO' },
    { code: 'MD', label: 'Managing Director' },
    { code: 'CHAIRMAN', label: 'Chairman' },
    { code: 'PARTNER', label: 'Partner' },
    { code: 'MANAGER', label: 'Manager' },
    { code: 'ACCOUNTANT', label: 'Accountant' },
    { code: 'SITE_ENGINEER', label: 'Site Engineer' },
    { code: 'PROJECT_MANAGER', label: 'Project Manager' },
    { code: 'AUTHORIZED_SIGNATORY', label: 'Authorized Signatory' },
    { code: 'OTHER', label: 'Other' }
  ];

  const emptyContactForm = { cntct_nm: '', dsgntn_cd: '', dsgntn_nm: '', phn_nu: '', alt_phn_nu: '', eml_tx: '', is_prmry_in: false };

  const loadContacts = useCallback(async () => {
    if (!contractor?.contractorId) return;
    setContactsLoading(true);
    try {
      const response = await axiosInstance.get(`ds-wms/contractors/${contractor.contractorId}/contacts`);
      setContacts(response.data.data || []);
    } catch (e) { console.warn('Failed to load contacts:', e); }
    finally { setContactsLoading(false); }
  }, [contractor?.contractorId]);

  useEffect(() => {
    if (isOpen && activeTab === 'contacts') loadContacts();
  }, [isOpen, activeTab, loadContacts]);

  const handleSaveContact = async () => {
    try {
      const payload = {
        ...contactForm,
        dsgntn_nm: CONTACT_DESIGNATIONS.find(d => d.code === contactForm.dsgntn_cd)?.label || contactForm.dsgntn_nm
      };
      if (editingContact) {
        await axiosInstance.put(`ds-wms/contractors/${contractor.contractorId}/contacts/${editingContact.cntrctr_cntct_id}`, payload);
      } else {
        await axiosInstance.post(`ds-wms/contractors/${contractor.contractorId}/contacts`, payload);
      }
      setShowContactForm(false);
      setEditingContact(null);
      setContactForm(emptyContactForm);
      loadContacts();
    } catch (e) { console.error('Failed to save contact:', e); }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!confirm('Remove this contact?')) return;
    try {
      await axiosInstance.delete(`ds-wms/contractors/${contractor.contractorId}/contacts/${contactId}`);
      loadContacts();
    } catch (e) { console.error('Failed to delete contact:', e); }
  };

  // Documents loading is handled by DocumentUploader component

  // Fetch works from API when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const cId = contractor?.contractorId;
    if (!cId || typeof cId !== 'number' || cId < 1) {
      console.warn('ContractorProfile: missing contractorId', contractor);
      return;
    }

    const fetchWorks = async () => {
      setLoading(true);
      try {
        const data = await contractorsApi.getWorks(cId);
        setContractorProjects(data || []);
      } catch (error) {
        console.error('Failed to fetch contractor works:', error);
        setContractorProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWorks();
    setStatusFilter(null);
    setActiveTab('details');
  }, [isOpen, contractor?.contractorId]);

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';
      case 'completed': return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
      case 'delayed': return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
      case 'on hold': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
      case 'cancelled': return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  // Calculate summary
  const totalProjects = contractorProjects.length;
  const totalValue = contractorProjects.reduce((sum, p) => sum + (p.sanctionedAmount || 0), 0);
  const inProgressCount = contractorProjects.filter(p => p.status === 'In Progress').length;
  const completedCount = contractorProjects.filter(p => p.status === 'Completed').length;
  const delayedCount = contractorProjects.filter(p => p.status === 'Delayed').length;

  // Filter projects based on selected status
  const filteredProjects = statusFilter
    ? contractorProjects.filter(p => p.status.toLowerCase() === statusFilter.toLowerCase())
    : contractorProjects;

  // Grid data with serial numbers
  const gridData = useMemo(() =>
    filteredProjects.map((p, i) => ({ ...p, sno: i + 1 })),
    [filteredProjects]
  );

  const worksColumns = useMemo(() => [
    { field: 'sno', columnName: 'S.No', columnType: 'number' as const, width: '60px', visible: true, alignment: 'center' as const },
    {
      field: 'workCode', columnName: 'Work Code', columnType: 'text' as const, width: '130px', visible: true, filter: true,
      cellRenderer: (row: any) => (
        <button
          onClick={(e) => { e.stopPropagation(); onWorkIdClick(String(row.workId)); }}
          className="font-mono text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded hover:bg-blue-200 cursor-pointer transition-colors"
        >
          {row.workCode || `WRK-${row.workId}`}
        </button>
      )
    },
    {
      field: 'workName', columnName: 'Work Name', columnType: 'text' as const, width: '280px', visible: true, filter: true,
      cellRenderer: (row: any) => (
        <div className="truncate" title={row.isPushkaralu ? `Pushkaralu-${row.workName}` : row.workName}>
          {row.isPushkaralu && <span className="font-semibold text-[#b45309]">Pushkaralu-</span>}
          <span className="text-gray-900">{row.workName}</span>
        </div>
      )
    },
    { field: 'workType', columnName: 'Work Type', columnType: 'text' as const, width: '140px', visible: true, filter: true },
    { field: 'udaName', columnName: 'UDA', columnType: 'text' as const, width: '120px', visible: true, filter: true },
    { field: 'districtName', columnName: 'District', columnType: 'text' as const, width: '110px', visible: true, filter: true },
    {
      field: 'sanctionedAmount', columnName: 'Sanctioned (₹ Cr)', columnType: 'number' as const, width: '150px', visible: true, alignment: 'right' as const,
      cellRenderer: (row: any) => (
        <span className="font-mono text-sm">{row.sanctionedAmount ? `₹${((row.sanctionedAmount || 0) / 10000000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Cr` : '-'}</span>
      )
    },
    {
      field: 'physicalProgress', columnName: 'Physical %', columnType: 'number' as const, width: '110px', visible: true, alignment: 'center' as const,
      cellRenderer: (row: any) => {
        const pct = (row.physicalProgress || 0) * 100;
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-orange-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span className="text-xs font-medium">{pct.toFixed(0)}%</span>
          </div>
        );
      }
    },
    {
      field: 'financialProgress', columnName: 'Financial %', columnType: 'number' as const, width: '100px', visible: true, alignment: 'center' as const,
      cellRenderer: (row: any) => (<span className="text-xs font-medium">{((row.financialProgress || 0) * 100).toFixed(0)}%</span>)
    },
    {
      field: 'status', columnName: 'Status', columnType: 'text' as const, width: '110px', visible: true, filter: true,
      cellRenderer: (row: any) => (<span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)}`}>{row.status}</span>)
    }
  ], []);

  // All hooks are above this line — only now is it safe to bail out, so the
  // hook order stays identical whether the modal is open or closed. (Previously
  // `if (!isOpen) return null` sat above these useMemo calls, so opening the
  // modal changed the hook count and React crashed the page to white.)
  if (!isOpen) return null;

  // Export to Excel (CSV format)
  const handleExportExcel = () => {
    const headers = [
      'S.No', 'Work Code', 'Work Name', 'Work Type/Sector', 'UDA', 'District',
      'Sector', 'Sanctioned Amt (₹ Cr)', 'Physical Progress (%)',
      'Financial Progress (%)', 'Start Date', 'Status'
    ];

    const csvData = filteredProjects.map((project, index) => [
      index + 1, project.workCode, `"${project.workName}"`, project.workType,
      project.udaName, project.districtName, project.sector, project.sanctionedAmount,
      (project.physicalProgress * 100).toFixed(1), (project.financialProgress * 100).toFixed(1),
      project.startDate, project.status
    ]);

    csvData.push(['', '', '', '', '', '', 'TOTAL',
      filteredProjects.reduce((sum, p) => sum + (p.sanctionedAmount || 0), 0).toFixed(2),
      '', '', '', ''
    ]);

    const csvContent = [
      `Contractor: ${contractor.name}`, '', headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `Contractor_${contractor.name.replace(/[^a-zA-Z0-9]/g, '_')}_Works_${todayYmd()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to PDF
  const handleExportPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Contractor Profile - ${contractor.name}</title>
        <style>
          @page { size: A4 landscape; margin: 10mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 10px; color: #333; padding: 15px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #2563eb; padding-bottom: 15px; }
          .header h1 { color: #2563eb; font-size: 20px; margin-bottom: 5px; }
          .header p { color: #666; font-size: 11px; }
          .contractor-info { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; margin-bottom: 15px; }
          .contractor-info h2 { color: #1e40af; font-size: 14px; margin-bottom: 8px; }
          .summary { display: flex; gap: 15px; margin-bottom: 15px; }
          .summary-item { flex: 1; background: #f1f5f9; padding: 10px; border-radius: 6px; text-align: center; }
          .summary-item .value { font-size: 16px; font-weight: 700; color: #2563eb; }
          .summary-item .label { font-size: 9px; color: #64748b; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          th { background: #2563eb; color: white; padding: 8px 6px; text-align: left; font-size: 9px; font-weight: 600; border: 1px solid #1d4ed8; }
          td { padding: 6px; border: 1px solid #e2e8f0; font-size: 9px; }
          tr:nth-child(even) { background: #f8fafc; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .font-semibold { font-weight: 600; }
          .status { padding: 2px 8px; border-radius: 12px; font-size: 8px; font-weight: 500; }
          .status-completed { background: #dcfce7; color: #166534; }
          .status-progress { background: #dbeafe; color: #1e40af; }
          .status-delayed { background: #fee2e2; color: #991b1b; }
          .status-hold { background: #fef9c3; color: #854d0e; }
          .totals-row { background: #dbeafe !important; font-weight: 600; }
          .totals-row td { border-top: 2px solid #2563eb; }
          .footer { text-align: center; font-size: 8px; color: #999; margin-top: 15px; padding-top: 10px; border-top: 1px solid #e2e8f0; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>WMS - Contractor Profile</h1>
          <p>Works Management System</p>
          <p style="margin-top: 5px;">Generated on: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
        </div>
        <div class="contractor-info"><h2>${contractor.name}</h2></div>
        <div class="summary">
          <div class="summary-item"><div class="value">${totalProjects}</div><div class="label">Total Works</div></div>
          <div class="summary-item"><div class="value">${completedCount}</div><div class="label">Completed</div></div>
          <div class="summary-item"><div class="value">${inProgressCount}</div><div class="label">In Progress</div></div>
          <div class="summary-item"><div class="value">${delayedCount}</div><div class="label">Delayed</div></div>
          <div class="summary-item"><div class="value">${formatCurrency(totalValue)}</div><div class="label">Total Sanctioned Value</div></div>
        </div>
        <table data-wms-table>
          <thead><tr>
            <th style="width: 30px;">S.No</th><th style="width: 80px;">Work Code</th><th>Work Name</th>
            <th>Work Type</th><th>UDA</th><th>District</th>
            <th class="text-right">Sanctioned (₹ Cr)</th><th class="text-center" style="width: 70px;">Physical %</th>
            <th class="text-center" style="width: 70px;">Financial %</th><th class="text-center" style="width: 70px;">Status</th>
          </tr></thead>
          <tbody>
            ${filteredProjects.map((project, index) => `
              <tr>
                <td class="text-center">${index + 1}</td>
                <td class="font-semibold" style="color: #2563eb;">${project.workCode}</td>
                <td>${project.workName}</td><td>${project.workType}</td>
                <td>${project.udaName}</td><td>${project.districtName}</td>
                <td class="text-right font-semibold">${project.sanctionedAmount?.toFixed(2) || '-'}</td>
                <td class="text-center">${(project.physicalProgress * 100).toFixed(1)}%</td>
                <td class="text-center">${(project.financialProgress * 100).toFixed(1)}%</td>
                <td class="text-center"><span class="status ${
                  project.status === 'Completed' ? 'status-completed' : project.status === 'In Progress' ? 'status-progress' :
                  project.status === 'Delayed' ? 'status-delayed' : 'status-hold'
                }">${project.status}</span></td>
              </tr>
            `).join('')}
            <tr class="totals-row">
              <td colspan="6" class="text-right font-semibold">TOTAL</td>
              <td class="text-right font-semibold">${filteredProjects.reduce((sum, p) => sum + (p.sanctionedAmount || 0), 0).toFixed(2)}</td>
              <td colspan="3"></td>
            </tr>
          </tbody>
        </table>
        <div class="footer">
          <p>WMS - Works Management System</p>
          <p>This is a computer-generated document. No signature required.</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
  };

  // ---- Tab content renderers ----

  const renderWorksTab = () => (
    <>
      {/* Summary Cards */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
        <div className="grid grid-cols-5 gap-3">
          <button onClick={() => setStatusFilter(null)}
            className={`rounded-lg p-2.5 border text-center transition-all cursor-pointer ${
              statusFilter === null ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 ring-2 ring-blue-400 ring-offset-1' : 'bg-blue-50 border-blue-200 hover:bg-blue-100'}`}>
            <p className="text-xl font-bold text-blue-700">{totalProjects}</p>
            <span className="text-xs text-blue-600">Total Works</span>
          </button>
          <button onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
            className={`rounded-lg p-2.5 border text-center transition-all cursor-pointer ${
              statusFilter === 'completed' ? 'bg-green-100 border-green-400 ring-2 ring-green-400 ring-offset-1' : 'bg-green-50 border-green-200 hover:bg-green-100'}`}>
            <p className="text-xl font-bold text-green-700">{completedCount}</p>
            <span className="text-xs text-green-600">Completed</span>
          </button>
          <button onClick={() => setStatusFilter(statusFilter === 'in progress' ? null : 'in progress')}
            className={`rounded-lg p-2.5 border text-center transition-all cursor-pointer ${
              statusFilter === 'in progress' ? 'bg-orange-100 border-orange-400 ring-2 ring-orange-400 ring-offset-1' : 'bg-orange-50 border-orange-200 hover:bg-orange-100'}`}>
            <p className="text-xl font-bold text-orange-700">{inProgressCount}</p>
            <span className="text-xs text-orange-600">In Progress</span>
          </button>
          <button onClick={() => setStatusFilter(statusFilter === 'delayed' ? null : 'delayed')}
            className={`rounded-lg p-2.5 border text-center transition-all cursor-pointer ${
              statusFilter === 'delayed' ? 'bg-red-100 border-red-400 ring-2 ring-red-400 ring-offset-1' : 'bg-red-50 border-red-200 hover:bg-red-100'}`}>
            <p className="text-xl font-bold text-red-700">{delayedCount}</p>
            <span className="text-xs text-red-600">Delayed</span>
          </button>
          <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-200 text-center">
            <p className="text-lg font-bold text-purple-700">{formatCurrency(totalValue)}</p>
            <span className="text-xs text-purple-600">Total Sanctioned Value</span>
          </div>
        </div>
        {statusFilter && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="text-gray-500">Filtering by:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusFilter)}`}>
              {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
            </span>
            <button onClick={() => setStatusFilter(null)} className="text-gray-400 hover:text-gray-600 ml-1"><i className="ri-close-circle-line"></i></button>
          </div>
        )}
      </div>

      {/* DataGrid */}
      <div className="flex-1 p-4 overflow-hidden">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border h-full overflow-hidden">
          <DataGrid
            dataSource={gridData}
            columns={worksColumns}
            gridConfig={{ title: 'Works List', pagination: true, minPageSizes: [10, 25, 50], enableSearchPanel: true, enableColumnChooser: true }}
            onRowClick={(row: any) => onWorkIdClick(String(row.workId))}
          />
        </div>
      </div>
    </>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details': return <ContractorDetailsTab record={record} loading={recordLoading} />;
      case 'works': return renderWorksTab();
      case 'payments': return <ContractorPaymentsTab contractorId={contractor.contractorId} onWorkIdClick={onWorkIdClick} />;
      case 'documents': return (
        <div className="p-4">
          <DocumentUploader handlerCd="CONTRACTOR_DOCS" refId={contractor.contractorId} refNm="contractor" />
        </div>
      );
      case 'workflows': return <ContractorWorkflowsTab contractorId={contractor.contractorId} onWorkIdClick={onWorkIdClick} />;
      case 'invoices': return <ContractorInvoicesTab contractorId={contractor.contractorId} onWorkIdClick={onWorkIdClick} />;
      case 'users': return (
        <div className="p-4 space-y-3">
          {/* Add User button */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Portal Users ({users.length})</h3>
            <button onClick={() => { setEditingUser(null); setUserForm({ usr_nm: '', dsply_nm: '', fst_nm: '', lst_nm: '', eml_tx: '', phn_nu: '', rle_cd: 'SUB', dsgns_tx: '', is_prmry_in: false, sts_cd: 'ACTIVE' }); setShowUserForm(true); }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5">
              <i className="ri-user-add-line"></i>Add User
            </button>
          </div>

          {/* Add/Edit User Form */}
          {showUserForm && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-blue-800">{editingUser ? 'Edit User' : 'New User'}</h4>
                <button onClick={() => { setShowUserForm(false); setEditingUser(null); }} className="text-gray-400 hover:text-gray-600"><i className="ri-close-line"></i></button>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Username *</label>
                  <input type="text" value={userForm.usr_nm} onChange={(e) => setUserForm({ ...userForm, usr_nm: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="login.username" disabled={!!editingUser} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">First Name *</label>
                  <input type="text" value={userForm.fst_nm} onChange={(e) => setUserForm({ ...userForm, fst_nm: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="First name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Last Name</label>
                  <input type="text" value={userForm.lst_nm} onChange={(e) => setUserForm({ ...userForm, lst_nm: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="Last name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">User Type</label>
                  <select value={userForm.rle_cd} onChange={(e) => setUserForm({ ...userForm, rle_cd: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm">
                    <option value="MAIN">Main (Admin)</option>
                    <option value="SUB">Sub User</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Designation</label>
                  <input type="text" value={userForm.dsgns_tx} onChange={(e) => setUserForm({ ...userForm, dsgns_tx: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="e.g., Site Manager, Owner" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Email</label>
                  <input type="email" value={userForm.eml_tx} onChange={(e) => setUserForm({ ...userForm, eml_tx: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="email@example.com" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Mobile *</label>
                  <input type="text" value={userForm.phn_nu} onChange={(e) => setUserForm({ ...userForm, phn_nu: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="9876543210" />
                </div>
              </div>
              {editingUser && (
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Status</label>
                  <select value={userForm.sts_cd} onChange={(e) => setUserForm({ ...userForm, sts_cd: e.target.value })}
                    className="w-32 border rounded-lg px-3 py-1.5 text-sm">
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="BLOCKED">Blocked</option>
                  </select>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button onClick={handleSaveUser} disabled={!userForm.usr_nm || !userForm.dsply_nm}
                  className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {editingUser ? 'Update' : 'Create User'}
                </button>
                <button onClick={() => { setShowUserForm(false); setEditingUser(null); }}
                  className="px-4 py-1.5 border rounded-lg text-sm hover:bg-gray-100">Cancel</button>
              </div>
            </div>
          )}

          {/* Users DataGrid */}
          {usersLoading ? (
            <TableSkeleton />
          ) : (
            <DataGrid
              dataSource={users.map((u: any, i: number) => ({ ...u, sno: i + 1 }))}
              columns={[
                { field: 'sno', columnName: 'S.No', columnType: 'number' as const, width: '50px', visible: true, alignment: 'center' as const },
                { field: 'usr_nm', columnName: 'Username', columnType: 'text' as const, width: '140px', visible: true,
                  cellRenderer: (row: any) => <span className="font-mono text-sm text-blue-700">{row.usr_nm}</span>
                },
                { field: 'dsply_nm', columnName: 'Name', columnType: 'text' as const, width: '160px', visible: true,
                  cellRenderer: (row: any) => (
                    <div>
                      <span className="font-medium text-sm">{row.dsply_nm}</span>
                      {row.is_prmry_in && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">Primary</span>}
                    </div>
                  )
                },
                { field: 'rle_cd', columnName: 'Type', columnType: 'text' as const, width: '80px', visible: true,
                  cellRenderer: (row: any) => (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${row.rle_cd === 'MAIN' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{row.rle_cd === 'MAIN' ? 'Main' : 'Sub'}</span>
                  )
                },
                { field: 'dsgns_tx', columnName: 'Designation', columnType: 'text' as const, width: '120px', visible: true,
                  cellRenderer: (row: any) => <span className="text-xs text-gray-600">{row.dsgns_tx || '-'}</span>
                },
                { field: 'eml_tx', columnName: 'Email', columnType: 'text' as const, width: '180px', visible: true,
                  cellRenderer: (row: any) => <span className="text-xs text-gray-600">{row.eml_tx || '-'}</span>
                },
                { field: 'phn_nu', columnName: 'Phone', columnType: 'text' as const, width: '110px', visible: true },
                { field: 'lst_lgn_ts', columnName: 'Last Login', columnType: 'text' as const, width: '140px', visible: true,
                  cellRenderer: (row: any) => row.lst_lgn_ts ? (
                    <div>
                      <span className="text-xs text-gray-700">{row.lst_lgn_ts}</span>
                      <p className="text-[10px] text-gray-400">{row.lgn_cnt_nu} logins</p>
                    </div>
                  ) : <span className="text-xs text-gray-300 italic">Never logged in</span>
                },
                { field: 'sts_cd', columnName: 'Status', columnType: 'text' as const, width: '80px', visible: true,
                  cellRenderer: (row: any) => (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${row.sts_cd === 'ACTIVE' ? 'bg-green-50 text-green-700' : row.sts_cd === 'BLOCKED' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{row.sts_cd}</span>
                  )
                },
                { field: 'actions', columnName: '', columnType: 'text' as const, width: '60px', visible: true,
                  cellRenderer: (row: any) => (
                    <button onClick={() => {
                      const nameParts = (row.dsply_nm || '').trim().split(' ');
                      setEditingUser(row);
                      setUserForm({ usr_nm: row.usr_nm, dsply_nm: row.dsply_nm, fst_nm: nameParts[0] || '', lst_nm: nameParts.slice(1).join(' ') || '', eml_tx: row.eml_tx || '', phn_nu: row.phn_nu || '', rle_cd: row.rle_cd, dsgns_tx: row.dsgns_tx || '', is_prmry_in: row.is_prmry_in, sts_cd: row.sts_cd });
                      setShowUserForm(true);
                    }} className="text-blue-600 hover:text-blue-800 text-xs"><i className="ri-edit-line mr-0.5"></i>Edit</button>
                  )
                },
              ]}
              gridConfig={{ pagination: false, enableSearchPanel: false }}
            />
          )}
        </div>
      );
      case 'contacts': return (
        <div className="p-4 space-y-3">
          {/* Add Contact button */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Contacts ({contacts.length})</h3>
            <button onClick={() => { setEditingContact(null); setContactForm(emptyContactForm); setShowContactForm(true); }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5">
              <i className="ri-contacts-line"></i>Add Contact
            </button>
          </div>

          {/* Add/Edit Contact Form */}
          {showContactForm && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-blue-800">{editingContact ? 'Edit Contact' : 'New Contact'}</h4>
                <button onClick={() => { setShowContactForm(false); setEditingContact(null); }} className="text-gray-400 hover:text-gray-600"><i className="ri-close-line"></i></button>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Name *</label>
                  <input type="text" value={contactForm.cntct_nm} onChange={(e) => setContactForm({ ...contactForm, cntct_nm: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="Contact person name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Designation</label>
                  <select value={contactForm.dsgntn_cd} onChange={(e) => {
                    const d = CONTACT_DESIGNATIONS.find(d => d.code === e.target.value);
                    setContactForm({ ...contactForm, dsgntn_cd: e.target.value, dsgntn_nm: d?.label || '' });
                  }} className="w-full border rounded-lg px-3 py-1.5 text-sm">
                    <option value="">Select...</option>
                    {CONTACT_DESIGNATIONS.map((d) => (<option key={d.code} value={d.code}>{d.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Phone *</label>
                  <input type="text" value={contactForm.phn_nu} onChange={(e) => setContactForm({ ...contactForm, phn_nu: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="9876543210" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Alternate Phone</label>
                  <input type="text" value={contactForm.alt_phn_nu} onChange={(e) => setContactForm({ ...contactForm, alt_phn_nu: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="Optional" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Email</label>
                  <input type="email" value={contactForm.eml_tx} onChange={(e) => setContactForm({ ...contactForm, eml_tx: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="email@example.com" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={contactForm.is_prmry_in} onChange={(e) => setContactForm({ ...contactForm, is_prmry_in: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                    <span className="text-sm text-gray-700">Primary Contact</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={handleSaveContact} disabled={!contactForm.cntct_nm.trim() || !contactForm.phn_nu.trim()}
                  className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {editingContact ? 'Update' : 'Add Contact'}
                </button>
                <button onClick={() => { setShowContactForm(false); setEditingContact(null); }}
                  className="px-4 py-1.5 border rounded-lg text-sm hover:bg-gray-100">Cancel</button>
              </div>
            </div>
          )}

          {/* Contacts DataGrid */}
          {contactsLoading ? (
            <TableSkeleton />
          ) : (
            <DataGrid
              dataSource={contacts.map((c: any, i: number) => ({ ...c, sno: i + 1 }))}
              columns={[
                { field: 'sno', columnName: 'S.No', columnType: 'number' as const, width: '50px', visible: true, alignment: 'center' as const },
                { field: 'cntct_nm', columnName: 'Name', columnType: 'text' as const, width: '180px', visible: true,
                  cellRenderer: (row: any) => (
                    <div>
                      <span className="font-medium text-sm">{row.cntct_nm}</span>
                      {row.is_prmry_in && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">Primary</span>}
                    </div>
                  )
                },
                { field: 'dsgntn_nm', columnName: 'Designation', columnType: 'text' as const, width: '160px', visible: true,
                  cellRenderer: (row: any) => <span className="text-xs text-gray-600">{row.dsgntn_nm || '-'}</span>
                },
                { field: 'phn_nu', columnName: 'Phone', columnType: 'text' as const, width: '130px', visible: true,
                  cellRenderer: (row: any) => (
                    <div>
                      <span className="text-sm">{row.phn_nu}</span>
                      {row.alt_phn_nu && <p className="text-[10px] text-gray-400">Alt: {row.alt_phn_nu}</p>}
                    </div>
                  )
                },
                { field: 'eml_tx', columnName: 'Email', columnType: 'text' as const, width: '200px', visible: true,
                  cellRenderer: (row: any) => <span className="text-xs text-gray-600">{row.eml_tx || '-'}</span>
                },
                { field: 'actions', columnName: '', columnType: 'text' as const, width: '100px', visible: true,
                  cellRenderer: (row: any) => (
                    <div className="flex items-center gap-2">
                      <button onClick={() => {
                        setEditingContact(row);
                        setContactForm({ cntct_nm: row.cntct_nm || '', dsgntn_cd: row.dsgntn_cd || '', dsgntn_nm: row.dsgntn_nm || '', phn_nu: row.phn_nu || '', alt_phn_nu: row.alt_phn_nu || '', eml_tx: row.eml_tx || '', is_prmry_in: row.is_prmry_in || false });
                        setShowContactForm(true);
                      }} className="text-blue-600 hover:text-blue-800 text-xs"><i className="ri-edit-line mr-0.5"></i>Edit</button>
                      <button onClick={() => handleDeleteContact(row.cntrctr_cntct_id)}
                        className="text-red-500 hover:text-red-700 text-xs"><i className="ri-delete-bin-line mr-0.5"></i>Del</button>
                    </div>
                  )
                },
              ]}
              gridConfig={{ pagination: false, enableSearchPanel: false }}
            />
          )}
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] ${isFullscreen ? 'p-0' : 'p-4'}`}>
      <div className={`bg-gray-100 dark:bg-gray-900 shadow-2xl overflow-hidden flex flex-col ${isFullscreen ? 'w-full h-full rounded-none' : 'rounded-xl w-full max-w-6xl max-h-[90vh]'}`}>
        {/* Header */}
        <div className={`${WMS_POPUP_HEADER} flex-wrap gap-y-1`}>
            <div className="flex items-center gap-2 min-w-0">
              <div className={WMS_POPUP_ICON}>
                <i className="ri-building-2-fill"></i>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className={WMS_POPUP_TITLE}>{contractor.name}</h2>
                  {contractor.contractorCode && (
                    <span className="px-2 py-px bg-white/20 rounded text-[10px] font-mono flex-shrink-0">{contractor.contractorCode}</span>
                  )}
                </div>
                <p className={WMS_POPUP_SUBTITLE}>{totalProjects} works assigned</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => setShowEditForm(true)} className={WMS_POPUP_BTN} title="Edit Contractor">
                <i className="ri-edit-line"></i>Edit
              </button>
              <button onClick={() => setIsFullscreen(!isFullscreen)} className={WMS_POPUP_CLOSE} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
              </button>
              {activeTab === 'works' && (
                <button onClick={handleExportPDF} className={WMS_POPUP_CLOSE} title="Export PDF">
                  <i className="ri-file-pdf-line text-base"></i>
                </button>
              )}
              <button onClick={onClose} className={WMS_POPUP_CLOSE} title="Close">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

          {/* Contact & Address Details */}
          <div className="w-full flex flex-wrap gap-x-5 gap-y-0.5 text-[11px] leading-tight text-white/80 pb-0.5">
            {contractor.contactPerson && (
              <span className="flex items-center gap-1.5"><i className="ri-user-line text-xs"></i>{contractor.contactPerson}</span>
            )}
            {contractor.phone && (
              <span className="flex items-center gap-1.5"><i className="ri-phone-line text-xs"></i>{contractor.phone}</span>
            )}
            {contractor.email && (
              <span className="flex items-center gap-1.5"><i className="ri-mail-line text-xs"></i>{contractor.email}</span>
            )}
            {(contractor.address || contractor.city) && (
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-xs"></i>
                {[contractor.address, contractor.city, contractor.state, contractor.pinCode].filter(Boolean).join(', ')}
              </span>
            )}
            {contractor.registrationNo && (
              <span className="flex items-center gap-1.5"><i className="ri-file-text-line text-xs"></i>Reg: {contractor.registrationNo}</span>
            )}
            {contractor.gstNo && (
              <span className="flex items-center gap-1.5"><i className="ri-government-line text-xs"></i>GST: {contractor.gstNo}</span>
            )}
          </div>
        </div>

        {/* Tab Bar */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex px-4">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content — Works holds a DataGrid that scrolls itself; every other tab is
            plain content (nine document cards, the users list, contacts) and was clipped
            under the footer with no way to reach it, so it scrolls here instead. */}
        <div className={`flex min-h-0 flex-1 flex-col ${activeTab === 'works' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            {activeTab === 'works' && (
              <>
                <button onClick={handleExportExcel}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-all">
                  <i className="ri-file-excel-2-line text-lg"></i><span>Excel</span>
                </button>
                <button onClick={handleExportPDF}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all">
                  <i className="ri-file-pdf-line text-lg"></i><span>PDF</span>
                </button>
              </>
            )}
          </div>
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 transition-colors font-medium">
            Close
          </button>
        </div>
      </div>
      {/* Edit Form */}
      <ContractorForm
        isOpen={showEditForm}
        onClose={() => setShowEditForm(false)}
        onSuccess={() => { setShowEditForm(false); setRecordNonce(n => n + 1); }}
        contractorId={contractor?.contractorId}
      />
      {!onWorkIdClickProp && workModalId && (
        <WorkProfile isOpen onClose={() => setWorkModalId('')} workId={workModalId} />
      )}
    </div>
  );
}
